import { AccountFlag, MappingStatus } from "../api/fetchAccountMappings/types";

export type FilterTab = "review" | "ai_mapped" | "resolved" | "all";

export const FILTER_TABS: { id: FilterTab; label: string }[] = [
  { id: "review", label: "Needs review" },
  { id: "ai_mapped", label: "AI mapped" },
  { id: "resolved", label: "Confirmed" },
  { id: "all", label: "All accounts" },
];

export const STATUS_LABELS: Record<MappingStatus, string> = {
  unmapped: "Unmapped",
  ai_mapped: "AI mapped",
  review: "Review",
  overridden: "Overridden",
  confirmed: "Confirmed",
};

/**
 * Status and confidence are colour-coded rather than neutral. This is a
 * deliberate departure from the "neutral badges only" rule in
 * frontend/CLAUDE.md: on a 44-row table the whole point of these labels is to
 * be scannable at a glance, and shape alone does not carry that. Colours follow
 * the mapping prototype.
 */
/**
 * The "a machine did this" treatment, shared by the AI mapped badge and the
 * Auto-map button that produces those rows.
 *
 * A gradient ring around a flat fill, which CSS cannot express with
 * border-color. Two stacked backgrounds do it: the fill clipped to padding-box,
 * the gradient clipped to border-box, showing through only where the
 * transparent border sits. Both must live in one `background` shorthand, or the
 * second declaration wipes the first — which is also why the dark variant
 * restates the ring rather than overriding the fill alone.
 */
export const AI_GRADIENT_RING_CLASSES = [
  "border-2 border-transparent",
  "[background:linear-gradient(#ffffff,#ffffff)_padding-box,linear-gradient(to_right,#ec4899,#8b5cf6,#22d3ee)_border-box]",
  "text-indigo-800",
  "dark:[background:linear-gradient(#241f47,#241f47)_padding-box,linear-gradient(to_right,#ec4899,#8b5cf6,#22d3ee)_border-box]",
  "dark:text-white",
].join(" ");

export const STATUS_BADGE_CLASSES: Record<MappingStatus, string> = {
  unmapped:
    "bg-rose-100 text-rose-700 hover:bg-rose-100 dark:bg-rose-950 dark:text-rose-200",
  // The one badge signalling "a machine did this, nobody has confirmed it" — so
  // it gets the AI treatment rather than a flat pastel like the states a person
  // moves a row through.
  ai_mapped: AI_GRADIENT_RING_CLASSES,
  review:
    "bg-amber-100 text-amber-700 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-200",
  overridden:
    "bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-950 dark:text-blue-200",
  confirmed:
    "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950 dark:text-emerald-200",
};

/**
 * Rows that still need a decision are tinted so they can be found by scrolling
 * rather than by reading every status badge. Every row carries the left border
 * so the text does not shift by 4px when a row changes state.
 *
 * Side-specific `border-l-*` colours are deliberate — a bare `border-rose-400`
 * would also repaint the row's bottom border.
 */
export const STATUS_ROW_CLASSES: Record<MappingStatus, string> = {
  unmapped: "border-l-4 border-l-rose-400 bg-rose-50/60 dark:bg-rose-950/20",
  review: "border-l-4 border-l-amber-400 bg-amber-50/50 dark:bg-amber-950/20",
  ai_mapped: "border-l-4 border-l-transparent",
  overridden: "border-l-4 border-l-transparent",
  confirmed: "border-l-4 border-l-transparent",
};

interface AccountFlagCopy {
  label: string;
  text: string;
  isWarning: boolean;
}

/**
 * Warnings mean the mapping alone cannot produce a correct KPI and someone has
 * to decide something. Notes just explain what happened.
 *
 * `label` is what the row shows; `text` is the explanation on hover. The same
 * flag repeats on every payroll account, so the row carries the short form.
 */
export const ACCOUNT_FLAG_COPY: Record<AccountFlag, AccountFlagCopy> = {
  payroll_split: {
    label: "May mix field & office wages",
    text: "Payroll posted to an expense account can mix field and office wages, which no account mapping can split. Allocate by employee, or split the account in QuickBooks — KPIs 1 and 2 both depend on this.",
    isWarning: true,
  },
  materials_in_overhead: {
    label: "Materials in overhead",
    text: "Looks like job materials posted to overhead. Left here they understate COGS and overstate gross margin — consider reclassing to a Cost of Goods Sold account in QuickBooks.",
    isWarning: true,
  },
  payroll_burden: {
    label: "Burden follows wages",
    text: "Payroll burden maps with the wages it belongs to — field burden to Field Labor, office burden to Office Labor.",
    isWarning: false,
  },
  excluded: {
    label: "Excluded from KPIs",
    text: "Excluded, so it never reaches a KPI. It also drops out of both sides of reconciliation.",
    isWarning: false,
  },
};

/**
 * Shown when the chosen CertainPath account sits in a different section than
 * QuickBooks' own classification. Deliberately not a flag — it follows from the
 * mapping the member picked, not from anything about the account itself.
 */
export const CROSS_TYPE_NOTICE = {
  label: "Reclassified",
  text: "This maps to a CertainPath account in a different section than QuickBooks classifies it under — intentional for cases like field wages posted to an Expense account, but worth a second look.",
};

/**
 * Excluding an account drops it from both sides of reconciliation, so a real
 * revenue account marked "Do Not Map" understates the KPI while every check
 * still passes. Confirm before excluding anything carrying this much balance.
 */
export const MATERIAL_BALANCE_THRESHOLD = 1000;

export const DO_NOT_MAP_CODE = "do_not_map";
